import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';

export interface RefreshInterval {
    label: string;
    seconds: number;
}

/**
 * Manual refresh plus an auto-refresh menu, ported from Customer Connect's provider dashboard so
 * the two products' tables behave the same way.
 *
 * <p>The auto-refresh button opens the interval menu rather than toggling: with Off as the first
 * entry, one control carries both the on/off decision and the interval, and choosing an interval
 * takes one click from any state instead of toggling on and then adjusting.
 */
@Component({
    selector: 'app-refresh-controls',
    standalone: true,
    imports: [CommonModule, MatMenuModule, MatTooltipModule],
    templateUrl: './refresh-controls.component.html',
    styleUrls: ['./refresh-controls.component.scss'],
    // The menu renders in an overlay outside this component, which encapsulated styles cannot reach.
    // Every rule in the stylesheet is nested under .refresh-controls or .refresh-interval-menu.
    encapsulation: ViewEncapsulation.None,
})
export class RefreshControlsComponent {
    /** Spins the manual button and blocks a second request while one is in flight. */
    @Input() loading = false;

    /** Current interval; 0 means auto-refresh is off. */
    @Input() seconds = 0;

    @Input() intervals: RefreshInterval[] = [
        { label: '15s', seconds: 15 },
        { label: '30s', seconds: 30 },
        { label: '1m', seconds: 60 },
        { label: '5m', seconds: 300 },
    ];

    /** Names the thing being refreshed in the tooltips and aria labels, e.g. "metrics". */
    @Input() subject = 'data';

    @Output() refresh = new EventEmitter<void>();

    @Output() secondsChange = new EventEmitter<number>();

    get enabled(): boolean {
        return this.seconds > 0;
    }

    /**
     * The interval alone, without the "Auto-refresh:" prefix the template renders beside it. The two
     * are separate elements so a narrow viewport can drop the prefix and keep the interval.
     */
    get intervalLabel(): string {
        if (!this.enabled) {
            return 'Off';
        }
        const match = this.intervals.find((i) => i.seconds === this.seconds);
        return match ? match.label : `${this.seconds}s`;
    }

    onRefresh() {
        if (!this.loading) {
            this.refresh.emit();
        }
    }

    /** Re-picking the current interval is a no-op rather than a timer restart. */
    select(seconds: number) {
        if (seconds !== this.seconds) {
            this.secondsChange.emit(seconds);
        }
    }
}
